import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Box, VStack, HStack, Text, Button, Spinner } from '@chakra-ui/react';
import { LuArrowLeft } from 'react-icons/lu';
import { AccountRecord, CreateAccountInput } from '@/types/account';
import { accountService } from '@/services/firebase/accountService';
import { useAuthStore } from '@/stores/authStore';
import { AppLayout } from '@/components/layout/AppLayout';
import { AccountForm } from '@/components/account/AccountForm';
import { InitialPasswordDialog } from '@/components/account/InitialPasswordDialog';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogActionTrigger,
} from '@/components/ui/dialog';
import { toaster } from '@/components/ui/toaster';

/** 発行パスワードの表示状態（一度閉じたら破棄され再表示不可） */
interface IssuedPassword {
  title: string;
  email: string;
  password: string;
  navigateToListOnClose: boolean;
}

/**
 * アカウント作成・編集ページ
 * /admin/accounts/new と /admin/accounts/:uid/edit を兼用
 */
export const AccountFormPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { uid } = useParams<{ uid: string }>();
  const { user } = useAuthStore();

  const isEditMode = !!uid;

  const [initialData, setInitialData] = useState<AccountRecord | undefined>(
    (location.state as { account?: AccountRecord } | null)?.account
  );
  const [loadingAccount, setLoadingAccount] = useState(isEditMode && !initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [issuedPassword, setIssuedPassword] = useState<IssuedPassword | null>(null);

  // パスワード再発行
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // 編集時、location.state に無ければ一覧から該当アカウントを取得（直リンク対応）
  useEffect(() => {
    if (!isEditMode || initialData) return;

    let cancelled = false;
    (async () => {
      try {
        const accounts = await accountService.listAccounts();
        const found = accounts.find((a) => a.uid === uid);
        if (cancelled) return;
        if (!found) {
          toaster.error({ title: '対象のアカウントが見つかりません' });
          navigate('/admin/accounts');
          return;
        }
        setInitialData(found);
      } catch (error) {
        if (!cancelled) {
          toaster.error({
            title: 'アカウント情報の取得に失敗しました',
            description: error instanceof Error ? error.message : undefined,
          });
          navigate('/admin/accounts');
        }
      } finally {
        if (!cancelled) setLoadingAccount(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, uid]);

  const handleSubmit = async (data: CreateAccountInput) => {
    setIsSubmitting(true);
    try {
      if (isEditMode && uid) {
        await accountService.updateAccount({ uid, ...data });
        toaster.success({ title: 'アカウントを更新しました' });
        navigate('/admin/accounts');
      } else {
        const result = await accountService.createAccount(data);
        // 初回パスワードはこのダイアログでのみ表示される（stateを閉じたら破棄）
        setIssuedPassword({
          title: 'アカウントを作成しました',
          email: data.email,
          password: result.initialPassword,
          navigateToListOnClose: true,
        });
      }
    } catch (error) {
      toaster.error({
        title: isEditMode ? 'アカウントの更新に失敗しました' : 'アカウントの作成に失敗しました',
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    if (!uid || !initialData) return;

    setIsResetting(true);
    try {
      const newPassword = await accountService.resetAccountPassword(uid);
      setResetConfirmOpen(false);
      setIssuedPassword({
        title: 'パスワードを再発行しました',
        email: initialData.email,
        password: newPassword,
        navigateToListOnClose: false,
      });
    } catch (error) {
      toaster.error({
        title: 'パスワードの再発行に失敗しました',
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsResetting(false);
    }
  };

  const handlePasswordDialogClose = () => {
    const shouldNavigate = issuedPassword?.navigateToListOnClose;
    setIssuedPassword(null);
    if (shouldNavigate) {
      navigate('/admin/accounts');
    }
  };

  if (loadingAccount) {
    return (
      <AppLayout>
        <VStack gap={4} py={8}>
          <Spinner size="xl" />
          <Text>読み込み中...</Text>
        </VStack>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <VStack align="stretch" gap={6} maxW="xl">
        {/* ヘッダー */}
        <HStack gap={4}>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/accounts')}>
            <LuArrowLeft />
          </Button>
          <Text fontSize="2xl" fontWeight="bold">
            {isEditMode ? 'アカウント編集' : '新規アカウント作成'}
          </Text>
        </HStack>

        <AccountForm
          initialData={isEditMode ? initialData : undefined}
          isSubmitting={isSubmitting}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/admin/accounts')}
          showSelfEmailWarning={isEditMode && uid === user?.uid}
          disableRoleChange={isEditMode && uid === user?.uid}
        />

        {/* パスワード再発行（編集時のみ） */}
        {isEditMode && initialData && (
          <Box pt={4} borderTopWidth={1}>
            <Text fontSize="sm" fontWeight="medium" mb={2}>
              パスワード
            </Text>
            <Button variant="outline" size="sm" onClick={() => setResetConfirmOpen(true)}>
              パスワードを再発行
            </Button>
            <Text fontSize="xs" color="gray.600" mt={1}>
              新しい16文字のパスワードを発行し、一度だけ表示します
            </Text>
          </Box>
        )}
      </VStack>

      {/* パスワード再発行の確認ダイアログ */}
      {initialData && (
        <DialogRoot open={resetConfirmOpen} onOpenChange={(e) => setResetConfirmOpen(e.open)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>パスワードの再発行</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <VStack align="stretch" gap={3}>
                <Text>
                  {initialData.userName}（{initialData.email}）のパスワードを再発行しますか？
                </Text>
                <Text fontSize="sm" color="orange.600">
                  ⚠ 現在のパスワードは使用できなくなります。
                </Text>
              </VStack>
            </DialogBody>
            <DialogFooter>
              <DialogActionTrigger asChild>
                <Button variant="outline">キャンセル</Button>
              </DialogActionTrigger>
              <Button colorPalette="blue" onClick={handleResetPassword} loading={isResetting}>
                再発行する
              </Button>
            </DialogFooter>
          </DialogContent>
        </DialogRoot>
      )}

      {/* 発行パスワード表示ダイアログ（一度だけ） */}
      {issuedPassword && (
        <InitialPasswordDialog
          open={!!issuedPassword}
          title={issuedPassword.title}
          email={issuedPassword.email}
          password={issuedPassword.password}
          onClose={handlePasswordDialogClose}
        />
      )}
    </AppLayout>
  );
};

export default AccountFormPage;
